import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { STORAGE_KEYS } from "../../services/api";
import logo from "../../../assets/images/logo.png";
import {
  BarChart3,
  Package,
  Bell,
  User,
  LogOut,
  Menu,
  X,
  Calendar,
  Building,
  Activity,
  FileText,
  Settings,
  Droplet,
} from "lucide-react";

// User Type Detection
const getUserTypeFlags = () => {
  try {
    const bpartnerData = localStorage.getItem(STORAGE_KEYS.B_PARTNER);

    if (bpartnerData) {
      const data = JSON.parse(bpartnerData);

      // Check for user type flags in the data
      const userTypeFlags = {
        isBDO: !!data.isbdo,
        isHospital: !!data.isHospital,
        isCollection: !!(data.IsEmployee || data.IsVolunteer),
        isPublicUser: !!(
          data.isdonor &&
          !data.isbdo &&
          !data.isHospital &&
          !(data.IsEmployee || data.IsVolunteer)
        ),
      };

      // If no flags found, try to infer from other properties
      if (
        !userTypeFlags.isBDO &&
        !userTypeFlags.isHospital &&
        !userTypeFlags.isCollection
      ) {
        // Check if this is a hospital user based on name or other properties
        const name = data.Name || data.name || "";
        const identifier = data.identifier || "";
        const value = data.Value || data.value || "";
      }

      return userTypeFlags;
    }
  } catch (err) {
    console.error("Error parsing bpartner data:", err);
  }

  // If no b_partner data found, try to infer from user_data
  const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);

  if (userData) {
    try {
      const user = JSON.parse(userData);

      // Try to infer user type from name or other properties
      const userName = user.Name || user.username || "";
      const userValue = user.Value || user.value || "";

      // Check for hospital keywords
      if (
        userName.toLowerCase().includes("hospital") ||
        userValue.toLowerCase().includes("hospital")
      ) {
        return {
          isBDO: false,
          isHospital: true,
          isCollection: false,
          isPublicUser: false,
        };
      }

      // Check for BDO keywords (including academy and aster)
      if (
        userName.toLowerCase().includes("bdo") ||
        userName.toLowerCase().includes("blood") ||
        userName.toLowerCase().includes("academy") ||
        userName.toLowerCase().includes("aster") ||
        userValue.toLowerCase().includes("bdo") ||
        userValue.toLowerCase().includes("blood") ||
        userValue.toLowerCase().includes("academy") ||
        userValue.toLowerCase().includes("aster")
      ) {
        return {
          isBDO: true,
          isHospital: false,
          isCollection: false,
          isPublicUser: false,
        };
      }
    } catch (err) {
      console.error("Error parsing user data:", err);
    }
  }

  return {
    isBDO: false,
    isHospital: false,
    isCollection: false,
    isPublicUser: false,
  };
};

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userType, setUserType] = useState({ isBDO: false, isHospital: false });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const loadUserType = () => {
      const userTypeFlags = getUserTypeFlags();
      setUserType(userTypeFlags);
    };
    loadUserType();
  }, [user]); // Reload when user changes

  // Define navigation based on user type
  const getNavigation = () => {
    if (userType.isHospital) {
      return [
        { name: "Dashboard", href: "/dashboard", icon: BarChart3 },
        { name: "Blood Requests", href: "/blood-requests", icon: Droplet },
        { name: "Billing History", href: "/billing-history", icon: FileText },
        { name: "In/Out Records", href: "/inout-records", icon: Package },
        { name: "Notices", href: "/notifications", icon: Bell },
        { name: "Profile", href: "/profile", icon: User },
      ];
    } else if (userType.isBDO) {
      return [
        { name: "Dashboard", href: "/dashboard", icon: BarChart3 },
        { name: "Campaigns", href: "/campaigns", icon: Calendar },
        { name: "Notices", href: "/notifications", icon: Bell },
        { name: "Profile", href: "/profile", icon: User },
      ];
    } else {
      // Default navigation for unknown user types
      return [
        { name: "Dashboard", href: "/dashboard", icon: BarChart3 },
        { name: "Billing History", href: "/billing-history", icon: FileText },
        { name: "Notices", href: "/notifications", icon: Bell },
        { name: "Profile", href: "/profile", icon: User },
      ];
    }
  };

  const navigation = getNavigation();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/public-dashboard");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const isActive = (href) => location.pathname === href;

  const getUserTypeLabel = () => {
    if (userType.isHospital) return "Hospital";
    if (userType.isBDO) return "Blood Donation Organization";
    return "Administrator";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-80 bg-red-600 shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:inset-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-red-500">
          <div className="flex items-center">
            <div className="h-10 w-10 bg-white rounded-md p-1.5 border-2 border-white shadow-sm">
              <img
                src={logo}
                alt="BTSM"
                className="h-full w-full object-contain"
              />
            </div>
            <span className="ml-2 text-xl font-bold text-white">BTSM</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md text-red-200 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-6 px-3 pb-20">
          <div className="space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={`group flex items-center px-3 py-2 text-base font-medium rounded-md transition-colors duration-200 ${isActive(item.href)
                      ? "bg-red-700 text-white"
                      : "text-red-100 hover:bg-red-500 hover:text-white"
                    }`}
                >
                  <Icon
                    className={`mr-3 h-5 w-5 ${isActive(item.href)
                        ? "text-white"
                        : "text-red-200 group-hover:text-white"
                      }`}
                  />
                  {item.name}
                </a>
              );
            })}
          </div>
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-red-500 bg-red-600">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-red-500 rounded-full flex items-center justify-center">
              {userType.isHospital ? (
                <Building className="h-4 w-4 text-white" />
              ) : userType.isBDO ? (
                <Activity className="h-4 w-4 text-white" />
              ) : (
                <User className="h-4 w-4 text-white" />
              )}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-white truncate">
                {user?.Name || user?.username || "User"}
              </p>
              <p className="text-xs text-red-200">{getUserTypeLabel()}</p>
            </div>
            <button
              onClick={handleLogout}
              className="ml-2 p-2 rounded-md text-red-200 hover:text-white transition-colors duration-200"
              title="Sign out"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-80">
        {/* Top header */}
        <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </button>

            <div className="flex-1 lg:hidden" />

            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700">
                Welcome back, {user?.Name || user?.username || "User"}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4">
          <div className="mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
